import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queriesApi } from '../services/queriesService'
import normalizeText from '../utils/normalizeText'

/**
 * Hook para obtener herramientas de Contentful
 * Incluye funcionalidad de filtrado por categorías específicas
 *
 * @param filters - Array de filtros para categorizar las herramientas o objeto con propiedad filters
 * @returns {Object} Objeto con loading, error, datos procesados y funciones de filtrado
 */
export const useContentfulTools = (filters: string[] | { filters: string[] } = []) => {
	// Normalizar el parámetro filters para manejar tanto array como objeto
	const normalizedFilters = Array.isArray(filters) ? filters : filters.filters || []

	// Query con Tanstack Query
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'tools', normalizedFilters] as const,
		queryFn: queriesApi.getTools,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true
	})

	// Manejar errores
	if (error) {
		console.error('Error al obtener datos de Tools desde Contentful:', error)
	}

	// Mapeo de nombres de filtros
	const filterNames = useMemo(
		() => ({
			mother: 'maternas',
			hypertension: 'hipertension',
			diabetes: 'diabetes',
			mental: 'saludmental'
		}),
		[]
	)

	// Función de filtrado memoizada
	const filterData = useMemo(() => {
		return (data: any[], filters: string[]) => {
			// Validar que filters sea un array válido
			if (!Array.isArray(filters) || filters.length === 0) return false

			const normalFilters = filters
				?.map(e => normalizeText(e))
				?.map(e => {
					if (e.includes(filterNames.mother)) {
						return filterNames.mother
					}

					if (e.includes('embarazo')) {
						return filterNames.mother
					}
					if (e.includes(filterNames.hypertension)) {
						return filterNames.hypertension
					}
					return e
				})

			let filteredData = data
				?.map(e => ({ ...e, filtros: e.filtros?.map((j: string) => normalizeText(j)) || [] }))
				?.filter(e => {
					// Buscar por filtros
					const matchByFilters = e.filtros.find((k: string) => normalFilters[0] === k)

					// Buscar por link
					const matchByLink = e.link && normalFilters[0] === normalizeText(e.link)

					return matchByFilters || matchByLink
				})

			if (normalFilters.length > 0) {
				const verify = (e: string) =>
					e === filterNames?.diabetes ||
					e === filterNames?.hypertension ||
					e === filterNames?.mother ||
					e === filterNames?.mental

				const setFilter = (e: string) => normalFilters.slice(1, normalFilters.length).includes(e)
				if (normalFilters.find(e => verify(e))) {
					filteredData = filteredData?.filter(e => e.filtros.some((j: string) => setFilter(j)))
				}

				if (normalFilters.includes(filterNames.mental)) {
					filteredData?.sort(a =>
						a.filtros.some((f: string) => f.includes(filterNames.mental)) ? 1 : -1
					)
				}

				if (normalFilters.includes(filterNames.mother)) {
					filteredData?.sort(a =>
						a.filtros.map((f: string) => f.includes(filterNames.mother)) ? -1 : 1
					)
				}
			}

			return filteredData
		}
	}, [filterNames])

	// Procesar datos de herramientas
	const isContentTool = useMemo(() => {
		if (!rawData || isLoading || error) return false

		try {
			const toolsData =
				rawData?.homeCollection?.items?.[0]?.vistas?.herramientas?.beneficioCollection?.items

			// Validar que toolsData sea un array válido
			if (!Array.isArray(toolsData) || toolsData.length === 0) return false

			// Validar que filters sea un array válido
			if (!Array.isArray(normalizedFilters)) {
				console.warn('Filters no un array válido, usando array vacío')
				return toolsData
			}

			const result = filterData(toolsData, normalizedFilters)

			return result
		} catch (transformError) {
			console.error('Error al procesar herramientas filtradas:', transformError)
			return false
		}
	}, [rawData, isLoading, error, normalizedFilters, filterData])

	// Procesar todas las herramientas
	const isAllContentTool = useMemo(() => {
		if (!rawData || isLoading || error) return false

		try {
			const toolsData =
				rawData?.homeCollection?.items?.[0]?.vistas?.herramientas?.beneficioCollection?.items
			return toolsData || false
		} catch (transformError) {
			console.error('Error al procesar todas las herramientas:', transformError)
			return false
		}
	}, [rawData, isLoading, error])

	// Información del cache
	const cacheInfo = useMemo(
		() => ({
			isStale,
			isRefetching,
			lastFetch: rawData ? new Date() : null,
			cacheExpiry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
		}),
		[isStale, isRefetching, rawData]
	)

	return {
		// Datos principales (compatibilidad con hook original)
		loading: isLoading,
		isContentTool,
		isAllContentTool,

		// Funcionalidades adicionales
		error,
		refetch,
		isStale,
		isRefetching,
		cacheInfo,
		rawData,
		filterData
	}
}

export default useContentfulTools
